import { Location } from '@angular/common';
import { Component, inject, viewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faPrint } from '@fortawesome/free-solid-svg-icons';
import { Observable, filter, switchMap, take, tap } from 'rxjs';

import { EntityTypeConstants } from '../../common/EntityTypeConstants';
import { ExtendedProfile } from '../../common/ExtendedProfile';
import { Order } from '../../common/Order';
import { AsyncClickDirective } from '../../directives/async-click.directive';
import { CallApiService } from '../../services/api-action.service';
import { OrderService } from '../../services/order.service';
import { AppProfileService } from '../../services/profile.service';
import { SettingService } from '../../services/setting.service';
import { DEFAULT_RECEIPT_WIDTH_CM, ReceiptWatermark, receiptWidthCm } from './order-receipt';
import { OrderReceiptComponent } from './order-receipt.component';
import { ReceiptPrinter } from './receipt-printer.service';

/**
 * `/order-receipt/:id` (#205): one order as a receipt, previewed at the printer's width and
 * printed from here. Opened from the seller's order view.
 *
 * Printing records the print on the order (`PUT order/print/{id}`), and a recorded print makes
 * every later one a copy. A print the backend could not record is said so — the slip itself came
 * out, but the next one will not be marked.
 */
@Component({
  selector: 'app-order-receipt-page',
  imports: [OrderReceiptComponent, AsyncClickDirective, FontAwesomeModule],
  templateUrl: './order-receipt-page.component.html',
})
export class OrderReceiptPageComponent {
  private readonly location = inject(Location);
  private readonly orderService = inject(OrderService);
  private readonly settingService = inject(SettingService);
  private readonly callApiService = inject(CallApiService);
  private readonly printer = inject(ReceiptPrinter);

  protected readonly faPrint = faPrint;
  private readonly receipt = viewChild(OrderReceiptComponent);

  order: Order = null;
  profile: ExtendedProfile = null;
  widthCm = DEFAULT_RECEIPT_WIDTH_CM;

  constructor() {
    const id = inject(ActivatedRoute).snapshot.paramMap.get('id');
    this.callApiService.callWithToasts(
      this.orderService.getById(id),
      null,
      $localize`Der Auftrag konnte nicht geladen werden.`,
      (order) => (this.order = order),
    );

    // The width is the customer's; every user of it may read the row, admin or not.
    inject(AppProfileService)
      .currentProfile$.pipe(
        filter((profile) => !!profile),
        take(1),
        tap((profile) => (this.profile = profile)),
        switchMap((profile) =>
          this.settingService.getByReference(profile.customer.id, EntityTypeConstants.EntityTypeCustomer),
        ),
      )
      .subscribe({
        next: (settings) => (this.widthCm = receiptWidthCm(settings)),
        // Without the row the default width serves; a receipt is still worth printing.
        error: () => {},
      });
  }

  get watermark(): ReceiptWatermark | null {
    return this.order?.lastPrintDateTime ? 'copy' : null;
  }

  print(): Observable<unknown> {
    const order = this.order;
    return this.printer.print(this.receipt().sheet().nativeElement, this.widthCm).pipe(
      switchMap(() =>
        this.callApiService.callWithToasts$(
          this.orderService.markPrinted(order.id),
          null,
          $localize`Der Druck konnte nicht vermerkt werden. Ein weiterer Druck wird daher nicht als Kopie gekennzeichnet.`,
        ),
      ),
      tap(() => (this.order = { ...order, lastPrintDateTime: new Date() })),
    );
  }

  onBackClicked(): void {
    this.location.back();
  }
}
